import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

// 3D-looking spheres with simulated lighting + depth (parallax factor)
const SPHERES = [
  { x: 8, y: 18, size: 150, depth: 55, light: '#bae6fd', mid: '#38bdf8', dark: '#0369a1', dur: 9, delay: 0 },
  { x: 74, y: 12, size: 190, depth: 90, light: '#ddd6fe', mid: '#a78bfa', dark: '#6d28d9', dur: 11, delay: 1.2 },
  { x: 82, y: 62, size: 120, depth: 70, light: '#bbf7d0', mid: '#34d399', dark: '#047857', dur: 8, delay: 0.6 },
  { x: 20, y: 68, size: 90, depth: 40, light: '#e0f2fe', mid: '#7dd3fc', dark: '#0284c7', dur: 10, delay: 1.8 },
  { x: 52, y: 30, size: 60, depth: 120, light: '#fce7f3', mid: '#f0abfc', dark: '#a21caf', dur: 7, delay: 0.9 },
];

function Sphere({ s }) {
  return (
    <div
      className="absolute"
      style={{
        left: `${s.x}%`,
        top: `${s.y}%`,
        transform: `translate3d(calc(var(--mx, 0) * ${s.depth}px), calc(var(--my, 0) * ${s.depth}px), 0)`,
        transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1)',
        willChange: 'transform',
      }}
    >
      <motion.div
        animate={{ y: [0, -22, 0], rotate: [0, 8, 0] }}
        transition={{ duration: s.dur, repeat: Infinity, delay: s.delay, ease: 'easeInOut' }}
      >
        <div
          style={{
            width: s.size,
            height: s.size,
            borderRadius: '50%',
            background: `radial-gradient(circle at 32% 28%, ${s.light} 0%, ${s.mid} 42%, ${s.dark} 100%)`,
            boxShadow: `0 30px 60px -18px ${s.dark}66, inset -8px -10px 24px ${s.dark}55, inset 10px 12px 26px #ffffff55`,
            opacity: 0.9,
          }}
        />
      </motion.div>
    </div>
  );
}

export default function Scene3D({ className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = null;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const mx = e.clientX / window.innerWidth - 0.5;
        const my = e.clientY / window.innerHeight - 0.5;
        el.style.setProperty('--mx', mx.toFixed(3));
        el.style.setProperty('--my', my.toFixed(3));
        raf = null;
      });
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`fixed inset-0 overflow-hidden pointer-events-none ${className}`}
      style={{ zIndex: 0, perspective: '1200px' }}
    >
      {/* Base gradient */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 100% at 50% -10%, #f8fbff 0%, #ffffff 45%, #f6f8fc 100%)' }} />

      {/* Perspective 3D grid floor */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: '-20%',
          width: '200%',
          height: '75%',
          transform: 'translateX(-50%) rotateX(70deg)',
          transformOrigin: 'center bottom',
          backgroundImage:
            'linear-gradient(to right, rgba(37,99,235,0.13) 1px, transparent 1px), linear-gradient(to bottom, rgba(37,99,235,0.13) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'linear-gradient(to top, black 0%, transparent 72%)',
          WebkitMaskImage: 'linear-gradient(to top, black 0%, transparent 72%)',
          animation: 'grid-scroll 9s linear infinite',
        }}
      />

      {/* Perspective 3D grid ceiling (subtler) */}
      <div
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          top: '-22%',
          width: '200%',
          height: '55%',
          transform: 'translateX(-50%) rotateX(-70deg)',
          transformOrigin: 'center top',
          backgroundImage:
            'linear-gradient(to right, rgba(139,92,246,0.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.10) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'linear-gradient(to bottom, black 0%, transparent 75%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 75%)',
        }}
      />

      {/* Rotating wireframe ring */}
      <motion.svg
        className="absolute"
        style={{ left: '58%', top: '8%', width: 520, height: 520, opacity: 0.35 }}
        viewBox="0 0 200 200"
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
      >
        <defs>
          <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="50%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
        </defs>
        <ellipse cx="100" cy="100" rx="90" ry="34" fill="none" stroke="url(#ring-grad)" strokeWidth="0.6" />
        <ellipse cx="100" cy="100" rx="34" ry="90" fill="none" stroke="url(#ring-grad)" strokeWidth="0.6" />
        <circle cx="100" cy="100" r="88" fill="none" stroke="url(#ring-grad)" strokeWidth="0.5" strokeDasharray="2 6" />
      </motion.svg>

      {/* Ambient colored glows */}
      <div className="absolute" style={{ left: '5%', top: '10%', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,0.22), transparent 65%)', filter: 'blur(20px)' }} />
      <div className="absolute" style={{ right: '6%', bottom: '12%', width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,211,153,0.20), transparent 65%)', filter: 'blur(20px)' }} />
      <div className="absolute" style={{ left: '45%', top: '40%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(167,139,250,0.18), transparent 65%)', filter: 'blur(20px)' }} />

      {/* Floating 3D spheres with mouse parallax */}
      {SPHERES.map((s, i) => (
        <Sphere key={i} s={s} />
      ))}

      {/* Grain overlay for premium texture */}
      <div
        className="absolute inset-0"
        style={{
          opacity: 0.05,
          mixBlendMode: 'multiply',
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
    </div>
  );
}
